// Chunk-cached life overlay system
// Each chunk is a fixed-size offscreen canvas containing pixel-art life details.
// Uses LRU eviction and throttled rebuilds.

import { createRng, hashCoords } from './rng.mjs';
import { drawLifeForTile } from './lifeGen.mjs';
import { hexToPixel, hexKey, getNeighbor } from '../../math/hexMath.mjs';

const CHUNK_PX = 512;
const MAX_CHUNKS = 200;
const REBUILD_INTERVAL_MS = 3000; // re-evaluate chunks every 3s for life progression
const MAX_REBUILDS_PER_FRAME = 1;

export class LifeChunkCache {
  /**
   * @param {number} worldSeed
   * @param {number} hexSize
   */
  constructor(worldSeed, hexSize) {
    this.worldSeed = worldSeed;
    this.hexSize = hexSize;
    /** @type {Map<string, {canvas: OffscreenCanvas|HTMLCanvasElement, ctx: CanvasRenderingContext2D, lastBuiltMs: number, dirty: boolean, lastUsedMs: number}>} */
    this.chunks = new Map();
    /** @type {string[]} queue of chunk keys needing rebuild */
    this.rebuildQueue = [];
    /** @type {Map<string, object>} reference to placed tiles */
    this.tiles = new Map();
  }

  /** Update tile reference (called when tiles change) */
  setTiles(tiles) {
    this.tiles = tiles;
    // Mark all chunks dirty on tile change
    this.invalidateAll();
  }

  /** Mark a specific chunk as dirty */
  invalidateChunk(chunkKey) {
    const chunk = this.chunks.get(chunkKey);
    if (chunk) chunk.dirty = true;
    if (!this.rebuildQueue.includes(chunkKey)) {
      this.rebuildQueue.push(chunkKey);
    }
  }

  /** Mark all chunks dirty */
  invalidateAll() {
    for (const [key, chunk] of this.chunks) {
      chunk.dirty = true;
      if (!this.rebuildQueue.includes(key)) {
        this.rebuildQueue.push(key);
      }
    }
  }

  /** Invalidate chunks that contain a world-space point */
  invalidateAt(worldX, worldY) {
    const ck = this._chunkKeyForWorld(worldX, worldY);
    this.invalidateChunk(ck);
  }

  /** Get chunk key for a world position */
  _chunkKeyForWorld(wx, wy) {
    const cx = Math.floor(wx / CHUNK_PX);
    const cy = Math.floor(wy / CHUNK_PX);
    return `${cx},${cy}`;
  }

  /**
   * Process up to N chunk rebuilds this frame.
   * @param {number} nowMs
   */
  update(nowMs) {
    // Also queue time-based rebuilds for visible chunks
    for (const [key, chunk] of this.chunks) {
      if (!chunk.dirty && nowMs - chunk.lastBuiltMs > REBUILD_INTERVAL_MS) {
        chunk.dirty = true;
        if (!this.rebuildQueue.includes(key)) {
          this.rebuildQueue.push(key);
        }
      }
    }

    let rebuilt = 0;
    while (this.rebuildQueue.length > 0 && rebuilt < MAX_REBUILDS_PER_FRAME) {
      const key = this.rebuildQueue.shift();
      const chunk = this.chunks.get(key);
      if (!chunk || !chunk.dirty) continue;
      this._rebuildChunk(key, chunk, nowMs);
      rebuilt++;
    }

    // LRU eviction
    if (this.chunks.size > MAX_CHUNKS) {
      const entries = [...this.chunks.entries()];
      entries.sort((a, b) => a[1].lastUsedMs - b[1].lastUsedMs);
      while (this.chunks.size > MAX_CHUNKS * 0.8) {
        const [key] = entries.shift();
        this.chunks.delete(key);
      }
    }
  }

  /**
   * Rebuild a single chunk's offscreen canvas.
   */
  _rebuildChunk(key, chunk, nowMs) {
    const [cxStr, cyStr] = key.split(',');
    const cx = parseInt(cxStr, 10);
    const cy = parseInt(cyStr, 10);
    const worldX = cx * CHUNK_PX;
    const worldY = cy * CHUNK_PX;

    const { ctx } = chunk;
    ctx.clearRect(0, 0, CHUNK_PX, CHUNK_PX);

    // Find tiles whose centers fall within or near this chunk
    for (const [, tile] of this.tiles) {
      if (!tile.placed || !tile.coord) continue;
      const wp = hexToPixel(tile.coord.q, tile.coord.r, this.hexSize);

      // Check if tile is within chunk bounds (with hex size buffer)
      const buf = this.hexSize * 1.5;
      if (wp.x < worldX - buf || wp.x > worldX + CHUNK_PX + buf) continue;
      if (wp.y < worldY - buf || wp.y > worldY + CHUNK_PX + buf) continue;

      // Offset within chunk canvas
      const ox = wp.x - worldX;
      const oy = wp.y - worldY;

      const ageSec = (nowMs - (tile.placedAtMs || nowMs)) / 1000;

      const primaryType = this._getPrimaryType(tile);

      // Each side region gets its own life overlay based on effective type
      // For simplicity, draw life based on the primary type of the tile
      const tileSeed = tile.seed || hashCoords(tile.coord.q, tile.coord.r, this.worldSeed);
      const rng = createRng(hashCoords(tileSeed, Math.floor(nowMs / REBUILD_INTERVAL_MS), this.worldSeed));

      drawLifeForTile(ctx, primaryType, ox, oy, this.hexSize, tileSeed, ageSec, rng);
    }

    chunk.lastBuiltMs = nowMs;
    chunk.dirty = false;
  }

  _getPrimaryType(tile) {
    // Count occurrences, return most common
    const counts = {};
    for (const s of tile.sides) {
      counts[s] = (counts[s] || 0) + 1;
    }
    let maxType = tile.sides[0];
    let maxCount = 0;
    for (const [t, c] of Object.entries(counts)) {
      if (c > maxCount) { maxType = t; maxCount = c; }
    }
    return maxType;
  }

  /**
   * Get or create a chunk for the given chunk coordinates.
   */
  _getOrCreateChunk(cx, cy, nowMs) {
    const key = `${cx},${cy}`;
    let chunk = this.chunks.get(key);
    if (!chunk) {
      let canvas;
      if (typeof OffscreenCanvas !== 'undefined') {
        canvas = new OffscreenCanvas(CHUNK_PX, CHUNK_PX);
      } else {
        canvas = document.createElement('canvas');
        canvas.width = CHUNK_PX;
        canvas.height = CHUNK_PX;
      }
      const ctx = canvas.getContext('2d');
      chunk = { canvas, ctx, lastBuiltMs: 0, dirty: true, lastUsedMs: nowMs };
      this.chunks.set(key, chunk);
      if (!this.rebuildQueue.includes(key)) {
        this.rebuildQueue.push(key);
      }
    }
    chunk.lastUsedMs = nowMs;
    return chunk;
  }

  /**
   * Render the life layer onto the main life canvas.
   * @param {CanvasRenderingContext2D} targetCtx - the life canvas context
   * @param {object} cam - camera {x, y, zoom}
   * @param {number} canvasWidth
   * @param {number} canvasHeight
   * @param {number} nowMs
   */
  render(targetCtx, cam, canvasWidth, canvasHeight, nowMs) {
    // Compute visible world bounds
    const minWx = (0 - canvasWidth / 2) / cam.zoom + cam.x;
    const minWy = (0 - canvasHeight / 2) / cam.zoom + cam.y;
    const maxWx = (canvasWidth - canvasWidth / 2) / cam.zoom + cam.x;
    const maxWy = (canvasHeight - canvasHeight / 2) / cam.zoom + cam.y;

    // Determine which chunks are visible
    const cxMin = Math.floor(minWx / CHUNK_PX) - 1;
    const cxMax = Math.ceil(maxWx / CHUNK_PX) + 1;
    const cyMin = Math.floor(minWy / CHUNK_PX) - 1;
    const cyMax = Math.ceil(maxWy / CHUNK_PX) + 1;

    targetCtx.save();
    targetCtx.imageSmoothingEnabled = false;

    for (let cx = cxMin; cx <= cxMax; cx++) {
      for (let cy = cyMin; cy <= cyMax; cy++) {
        const chunk = this._getOrCreateChunk(cx, cy, nowMs);

        // World position of chunk top-left
        const wx = cx * CHUNK_PX;
        const wy = cy * CHUNK_PX;

        // Screen position (snap to integer for pixel crispness)
        const sx = Math.round((wx - cam.x) * cam.zoom + canvasWidth / 2);
        const sy = Math.round((wy - cam.y) * cam.zoom + canvasHeight / 2);
        const sw = Math.ceil(CHUNK_PX * cam.zoom);
        const sh = Math.ceil(CHUNK_PX * cam.zoom);

        targetCtx.drawImage(chunk.canvas, sx, sy, sw, sh);
      }
    }

    targetCtx.restore();
  }
}
